/**
 * - Handles the keyboard navigation for a flyout menu
 * - Sets focus to the first button in the flyout menu upon mounting
 * - Handles clicks outside the flyout menu
 * @param flyoutMenuId - The id of the flyout menu
 * @param toolbarId - The id of the toolbar that contains the flyout menu
 * @param close - The function to call when the flyout menu is closed
 * @returns A function to dispose of the event listener
 */
export function flyOutMenuHandler({
    flyoutMenuId,
    toolbarId,
    close,
}: {
    flyoutMenuId: string;
    toolbarId: string;
    close: () => void;
}) {
    // Wait for 200ms before focusing the first button to ensure the menu is fully rendered
    const buttonFocusTimeout = setTimeout(() => {
        const firstBtn = document.querySelector(`#${flyoutMenuId} button:not([disabled])`);
        (firstBtn as HTMLButtonElement)?.focus();
    }, 200);

    const focusNextOrPrevButton = (direction: "next" | "prev") => {
        const buttons = Array.from(
            document.querySelectorAll<HTMLButtonElement>(`#${flyoutMenuId} button:not([disabled])`),
        );
        if (buttons.length === 0) {
            return;
        }
        const currentIndex = buttons.findIndex((btn) => btn === document.activeElement);
        if (currentIndex === -1) {
            buttons[0].focus();
            return;
        }
        const nextIndex =
            direction === "next"
                ? (currentIndex + 1) % buttons.length
                : (currentIndex - 1 + buttons.length) % buttons.length;
        buttons[nextIndex].focus();
    };

    const handleKeyDownOuter = (event: KeyboardEvent) => {
        switch (event.code) {
            case "Escape":
                event.stopPropagation();
                event.preventDefault();
                close();
                break;
            case "ArrowDown":
                event.stopPropagation();
                event.preventDefault();
                focusNextOrPrevButton("next");
                break;
            case "ArrowUp":
                event.stopPropagation();
                event.preventDefault();
                focusNextOrPrevButton("prev");
                break;
        }
    };
    document.addEventListener("keydown", handleKeyDownOuter, { capture: true });

    const clickAwayHandler = (event: PointerEvent) => {
        if (
            !(event.target instanceof Element) ||
            event.target.closest(`#${flyoutMenuId}`) ||
            event.target.closest(`#${toolbarId}`)
        ) {
            return;
        }
        close();
    };
    // For some reason original click may trigger this click away event, so wrap it into a setTimeout
    const clickAwayTimeout = setTimeout(() => {
        document.addEventListener("pointerdown", clickAwayHandler);
    });

    return () => {
        clearTimeout(buttonFocusTimeout);
        document.removeEventListener("keydown", handleKeyDownOuter, { capture: true });
        document.removeEventListener("pointerdown", clickAwayHandler);
        clearTimeout(clickAwayTimeout);
    };
}
